import React, { useState, useEffect } from 'react';

export default function HostEarningsCalculator() {
    const [chargerPower, setChargerPower] = useState(7.4);
    const [customPower, setCustomPower] = useState(3.3);
    const [hoursAvailable, setHoursAvailable] = useState(12);
    const [pricePerUnit, setPricePerUnit] = useState(12);
    const [sessionsPerDay, setSessionsPerDay] = useState(2);

    const [results, setResults] = useState({
        daily: 0,
        monthly: 0,
        yearly: 0,
        unitsShared: 0,
        co2Offset: 0,
        monthlySavingsGuest: 0
    });

    useEffect(() => {
        // Assume average session is 2 hours, capped by hours available
        const avgSessionHours = 2;
        // Cap sessions so total charging time doesn't exceed available hours
        const maxSessions = Math.floor(hoursAvailable / avgSessionHours) || 1;
        const actualSessions = Math.min(sessionsPerDay, maxSessions);
        
        const powerToUse = chargerPower === 'custom' ? Number(customPower) : Number(chargerPower);
        const dailyUnits = powerToUse * avgSessionHours * actualSessions;
        
        const dailyEarnings = dailyUnits * Number(pricePerUnit);
        const monthlyEarnings = dailyEarnings * 26; // 26 days accounting for downtime
        const yearlyEarnings = monthlyEarnings * 12;
        const monthlyUnitsShared = dailyUnits * 26;
        const co2Saved = monthlyUnitsShared * 0.82; // kg

        // Assume public charging is ₹20/kWh. Guest savings = (20 - price) * units
        const publicRate = 20;
        const monthlySavingsGuest = Math.max(0, (publicRate - pricePerUnit)) * monthlyUnitsShared;

        setResults({
            daily: Math.round(dailyEarnings),
            monthly: Math.round(monthlyEarnings),
            yearly: Math.round(yearlyEarnings),
            unitsShared: Math.round(monthlyUnitsShared),
            co2Offset: Math.round(co2Saved),
            monthlySavingsGuest: Math.round(monthlySavingsGuest)
        });
    }, [chargerPower, customPower, hoursAvailable, pricePerUnit, sessionsPerDay]);

    return (
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
            <div className="bg-green-600 px-6 py-8 text-white text-center">
                <h2 className="text-3xl font-bold mb-2">Host Earnings Calculator</h2>
                <p className="text-green-100">See how much you can earn sharing your home charger.</p>
            </div>
            
            <div className="p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-2 gap-10">
                {/* Inputs Section */}
                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Charger Power</label>
                        <select 
                            className="w-full border-gray-300 rounded-lg shadow-sm focus:border-green-500 focus:ring-green-500 p-3 border"
                            value={chargerPower}
                            onChange={(e) => setChargerPower(e.target.value === 'custom' ? 'custom' : Number(e.target.value))}
                        >
                            <option value={3.3}>3.3 kW (15A Socket)</option>
                            <option value={7.4}>7.4 kW (AC Fast)</option>
                            <option value={11}>11 kW (AC Fast)</option>
                            <option value={22}>22 kW (AC Ultra Fast)</option>
                            <option value="custom">Custom</option>
                        </select>
                        {chargerPower === 'custom' && (
                            <div className="mt-3 flex items-center">
                                <input 
                                    type="number" 
                                    className="w-full border-gray-300 rounded-lg shadow-sm focus:border-green-500 focus:ring-green-500 p-3 border"
                                    value={customPower}
                                    onChange={(e) => setCustomPower(Number(e.target.value))}
                                    step="0.1"
                                    min="1"
                                />
                                <span className="ml-3 text-gray-500">kW</span>
                            </div>
                        )}
                    </div>

                    <div>
                        <div className="flex justify-between mb-2">
                            <label className="text-sm font-medium text-gray-700">Hours Available Per Day</label>
                            <span className="text-sm font-bold text-green-600">{hoursAvailable} hrs</span>
                        </div>
                        <input 
                            type="range" 
                            min="1" 
                            max="24" 
                            value={hoursAvailable}
                            onChange={(e) => setHoursAvailable(Number(e.target.value))}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-600"
                        />
                    </div>

                    <div>
                        <div className="flex justify-between mb-2">
                            <label className="text-sm font-medium text-gray-700">Expected Sessions Per Day</label>
                            <span className="text-sm font-bold text-green-600">{sessionsPerDay} sessions</span>
                        </div>
                        <input 
                            type="range" 
                            min="1" 
                            max="10" 
                            value={sessionsPerDay}
                            onChange={(e) => setSessionsPerDay(Number(e.target.value))}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-600"
                        />
                        <p className="text-xs text-gray-500 mt-1">Assumes average 2-hour charge per session.</p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Price Per Unit (₹/kWh)</label>
                        <div className="relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <span className="text-gray-500 sm:text-sm">₹</span>
                            </div>
                            <input 
                                type="number" 
                                className="w-full pl-8 border-gray-300 rounded-lg shadow-sm focus:border-green-500 focus:ring-green-500 p-3 border"
                                value={pricePerUnit}
                                onChange={(e) => setPricePerUnit(Number(e.target.value))}
                                min="1"
                            />
                        </div>
                    </div>
                </div>

                {/* Outputs Section */}
                <div className="bg-gray-50 rounded-xl p-6 sm:p-8 flex flex-col justify-center border border-gray-100">
                    <div className="text-center mb-8">
                        <p className="text-sm text-gray-500 font-medium uppercase tracking-wide">Estimated Monthly Earnings</p>
                        <p className="text-5xl font-extrabold text-gray-900 mt-2">₹{results.monthly.toLocaleString('en-IN')}</p>
                        <p className="text-sm text-gray-500 mt-2">₹{results.yearly.toLocaleString('en-IN')} / year</p>
                    </div>

                    <div className="space-y-4 mb-8">
                        <div className="flex justify-between items-center py-3 border-b border-gray-200">
                            <span className="text-gray-600">Daily Earnings</span>
                            <span className="font-semibold text-gray-900">₹{results.daily.toLocaleString('en-IN')}</span>
                        </div>
                        <div className="flex justify-between items-center py-3 border-b border-gray-200">
                            <span className="text-gray-600">Electricity Shared</span>
                            <span className="font-semibold text-gray-900">{results.unitsShared.toLocaleString('en-IN')} units/mo</span>
                        </div>
                        <div className="flex justify-between items-center py-3">
                            <span className="text-gray-600">CO₂ Offset Equivalent</span>
                            <span className="font-semibold text-green-600">{results.co2Offset.toLocaleString('en-IN')} kg/mo 🌿</span>
                        </div>
                    </div>

                    {/* Win-Win Visualizer */}
                    <div className="mb-8">
                        <p className="text-xs text-gray-500 text-center mb-3 font-medium uppercase">Community Impact</p>
                        <div className="flex flex-col space-y-3">
                            <div>
                                <div className="flex justify-between text-xs mb-1">
                                    <span className="font-semibold text-gray-700">You Earn</span>
                                    <span className="font-bold text-green-600">₹{results.monthly.toLocaleString('en-IN')}</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2.5">
                                    <div className="bg-green-500 h-2.5 rounded-full" style={{ width: '100%' }}></div>
                                </div>
                            </div>
                            <div>
                                <div className="flex justify-between text-xs mb-1">
                                    <span className="font-semibold text-gray-700">Your Guests Save</span>
                                    <span className="font-bold text-blue-600">₹{results.monthlySavingsGuest.toLocaleString('en-IN')}</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2.5">
                                    <div className="bg-blue-500 h-2.5 rounded-full" style={{ width: 'min(100%, 80%)' }}></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <a href="https://play.google.com/store/apps/details?id=com.niloy.peerchargerev" target="_blank" rel="noopener noreferrer" className="w-full block text-center bg-green-600 text-white font-bold py-4 px-6 rounded-xl hover:bg-green-700 transition-colors shadow-md hover:shadow-lg transform hover:-translate-y-0.5 duration-200">
                        List Your Charger — It's Free
                    </a>
                </div>
            </div>
        </div>
    );
}
